import React, { memo, useEffect } from 'react';
import { prop } from 'ramda';
import { useForm } from 'react-final-form';
import { clsx } from 'js/utils/classNames.util';
import { useUserSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentTranslations } from 'js/hooks/component';
import { EditableDropDown } from 'js/components/common/Fields/EditableDropDown';

import * as Types from './types';
import * as hooks from './hooks';
import { IMS_UNLIMITED_AMOUNT } from './constants';
import { useDynamicReducerSelector } from '../../store';
import { useFieldLabel } from '../ChangeFormField/hooks';

import style from './DepositLimits.scss';

export const DepositLimits: React.FC<Types.IDepositLimitsOptions> = memo(({ field, fieldId, className }) => {
    const { batch, change } = useForm();
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const limitsConfig = hooks.useDepositLimitsConfig(field);
    const limitsCurrency = useDynamicReducerSelector(prop('limitsCurrency'));
    const { infoTooltip: fieldTooltip, imsFieldDaily, imsFieldWeekly, imsFieldMonthly } = field;

    const hideLimits = isLoggedIn || !limitsConfig || !limitsConfig.isEnabled || !limitsCurrency;
    const infoTooltip = fieldTooltip && translate(fieldTooltip);
    const limitPrefix = 'playtech.mobile.system.form.limits.depositLimit';
    const placeholder = translate('playtech.mobile.system.form.limits.placeholder');
    const [dailyLabel, getDailyLabelText] = useFieldLabel(`${limitPrefix}.dailyLimit`);
    const [weeklyLabel, getWeeklyLabelText] = useFieldLabel(`${limitPrefix}.weeklyLimit`);
    const [monthlyLabel, getMonthLabelText] = useFieldLabel(`${limitPrefix}.monthlyLimit`);
    const maxValue = Boolean(limitsConfig?.noLimitsAllowed) && IMS_UNLIMITED_AMOUNT;

    useEffect(() => {
        if (hideLimits) {
            return;
        }

        const {
            isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled,
            limit: { dailyDefaultLimit = '', weeklyDefaultLimit = '', monthlyDefaultLimit = '' }
        } = limitsConfig;

        batch(() => {
            isDailyEnabled && change(imsFieldDaily, dailyDefaultLimit);
            isWeeklyEnabled && change(imsFieldWeekly, weeklyDefaultLimit);
            isMonthlyEnabled && change(imsFieldMonthly, monthlyDefaultLimit);
        });

        // We need to add limitsCurrency to the dependencies so that when the currency changes,
        // the limits fields are cleared regardless of whether the input data changes or not.
    }, [
        batch, change, hideLimits, limitsConfig, limitsCurrency,
        imsFieldDaily, imsFieldWeekly, imsFieldMonthly
    ]);

    if (hideLimits) {
        return null;
    }

    const {
        isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled,
        mode, dataList, displayValuesMap
    } = limitsConfig;

    return (
        <fieldset className={clsx([className, style.depositLimits, 'cms-form-deposit-limits'])}>
            {isDailyEnabled && (
                <EditableDropDown
                    mode={mode}
                    label={dailyLabel}
                    inputMode="decimal"
                    dataList={dataList}
                    name="daydepositlimit"
                    infoTooltip={infoTooltip}
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_daily-deposit-limit`}
                    aria-label={getDailyLabelText() || placeholder}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                />
            )}
            {isWeeklyEnabled && (
                <EditableDropDown
                    mode={mode}
                    inputMode="decimal"
                    dataList={dataList}
                    label={weeklyLabel}
                    name="weekdepositlimit"
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_weekly-deposit-limit`}
                    infoTooltip={!isDailyEnabled && infoTooltip}
                    aria-label={getWeeklyLabelText() || placeholder}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                />
            )}
            {isMonthlyEnabled && (
                <EditableDropDown
                    mode={mode}
                    inputMode="decimal"
                    dataList={dataList}
                    label={monthlyLabel}
                    name="monthdepositlimit"
                    className={style.control}
                    placeholder={placeholder}
                    {...maxValue && { maxValue }}
                    displayValuesMap={displayValuesMap}
                    id={`${fieldId}_monthly-deposit-limit`}
                    aria-label={getMonthLabelText() || placeholder}
                    currencySymbol={formatter.getCurrencySymbol(limitsCurrency)}
                    infoTooltip={!isDailyEnabled && !isWeeklyEnabled && infoTooltip}
                />
            )}
        </fieldset>
    );
});
